import { clsx, type ClassValue } from 'clsx'
import { customAlphabet } from 'nanoid'
import { twMerge } from 'tailwind-merge'
import { format as sqlFormatterFormat } from 'sql-formatter'
import { Message } from 'ai'
import type { Document } from '@/types/editor'
import { AllToolResults } from './llm-tools/all-tools'
import { AllToolCalls } from './llm-tools/all-tools'
import { v4 } from 'uuid'
import { MatterFileInfo } from '@/app/api/matter/files/types'
import { isKLSource, isKLSourceDocument, KLSource, KLSourceWebPage } from 'bridging'
import { isKLSourceCitationId, parseKLSourceCitationSourceId } from './knowledge-library/kl-source-citation-util'
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export const ONEHUNDRED_MEGABYTES = 100000000
export const SECOND = 1000;
export const MINUTE = 60 * SECOND;
export const HOUR = 60 * MINUTE;
export const TENMINUTES = 10 * MINUTE;
export const ONEDAY = 24 * 60 * MINUTE;

export const nanoid = customAlphabet(
  '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz',
  22
) // 22-character NanoID with approximately 131 bits of entropy

export async function fetcher<JSON = any>(
  input: RequestInfo,
  init?: RequestInit
): Promise<JSON> {
  const res = await fetch(input, init)

  if (!res.ok) {
    const json = await res.json()
    if (json.error) {
      const error = new Error(json.error) as Error & {
        status: number
      }
      error.status = res.status
      throw error
    } else {
      throw new Error('An unexpected error occurred')
    }
  }

  return res.json()
}

export function formatDate(input: string | number | Date): string {
  const date = new Date(input)
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  })
}

export function setMessageContent(message: Message, content: string) {
  if (typeof message.content === 'string') {
    message.content = content
  }
  if (message.parts?.length) {
    // Remove all existing text parts and add a single new text part
    const nonTextParts = message.parts?.filter((part) => part.type !== 'text') || []
    message.parts = [
      ...nonTextParts,
      {
        type: 'text',
        text: content
      }
    ]
  }
}

export function hasMessageContent(message: Message) : boolean {
  return Boolean(
    (typeof message.content === 'string' && message.content.length > 0) 
    || (message.parts && message.parts.length > 0 && message.parts.some((part) => part.type === 'text' && part.text && part.text.length > 0)))
}

export function isMessageLike(message: Message | null): boolean {
  if (!message) return false;
  return Boolean(
    typeof message.content === 'string' || (message.parts?.length ?? 0) > 0
  );
}

export function getMessageContent(message: Message) {
  if (message.content) {
    return message.content
  }

  if (message.parts?.length) {
    const textFromParts = message.parts
      ?.filter((part) => part.type === 'text')
      .map((part) => part.text)
      .join('\n')
      .trim();

    return textFromParts
  }

  return ''
}

export const getUserInitials = (name: string) => {
  const [firstName, lastName] = name.split(' ')
  return lastName ? `${firstName[0]}${lastName[0]}` : firstName.slice(0, 2)
}


export const getUserFirstName = (name: string) => {
  const [firstName, lastName] = name.split(' ')
  return firstName || lastName
}

export const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

export const trackingPrefix = {
  matter: 'm',
  chat: 'c',
  messageFeedback: 'mf',
  matterFiles: 'mfil',
  chatFeedback: 'cf',
  telemetry: 'mt',
  ingestedMatterAdmin: 'ima',
  ingestedMatterAdminFile: 'imaf',
  ingestedMatterAdminMatterEntityInfo: 'imame',
  ingestedMatterAdminFirmEntityInfo: 'imafe',
  fileMetadata: 'fm',
  refreshCache: 'rc',
  ingestionTracking: 'it',
  preview: 'p',
  healthcheck: 'hc',
}

export const supportedExtensions = [
  '.msg',
  '.eml',
  '.xlsx',
  '.htm',
  '.html',
  '.pdf',
  '.txt',
  '.csv',
  '.ppt',
  '.pptx',
  '.doc',
  '.docx',
  '.rtf',
  '.note',
  '.mp3',
  '.mp4',
  '.mpeg',
  '.mpga',
  '.m4a',
  '.wav',
  '.webm',
  '.md',
]

export const isPreProdEnvironment = process.env.NEXT_PUBLIC_SB_ENV === 'staging' || process.env.NEXT_PUBLIC_SB_ENV === 'dev'

export const isTestEnvironment = Boolean(
  process.env.PLAYWRIGHT_TEST_BASE_URL ||
  process.env.PLAYWRIGHT ||
  process.env.CI_PLAYWRIGHT,
);

export const getCountryCode = () => {

  let countryCode;
  switch (process.env.NEXT_PUBLIC_SB_REGION) {
    case "aus":
      countryCode = "AU";
      break;
    case "nova":
      countryCode = "US";
      break;
    case "lon":
      countryCode = "GB";
      break;
    default:
      countryCode = undefined;
      break;
  }

  return countryCode;
}

export const sqlFormat = (sql: string) => {
  try {
    const formatted = sqlFormatterFormat(sql, { language: 'postgresql' })
    // replace all instances of "<= >" in formatted with "<=>"
    return formatted.replace(/(<=\s+>)/g, '<=>')
  } catch (error: any) {
    return sql
  }
}

export const convertNewLinesToBr = (text?: string) => {
  return { __html: (text || '').replace(/\n/g, '<br />') };
};



export function sanitizeUIMessages(messages: Array<Message>): Array<Message> {
  // TODO: Restore toolInvocations filter when upgrading to new AI SDK
  const messagesBySanitizedToolInvocations = messages.map((message) => {
    if (message.role !== 'assistant') return message;

    // if (!message.toolInvocations) return message;

    const toolResultIds: Array<string> = [];

    // for (const toolInvocation of message.toolInvocations) {
    //   if (toolInvocation.state === 'result') {
    //     toolResultIds.push(toolInvocation.toolCallId);
    //   }
    // }

    // const sanitizedToolInvocations = message.toolInvocations.filter(
    //   (toolInvocation) =>
    //     toolInvocation.state === 'result' ||
    //     toolResultIds.includes(toolInvocation.toolCallId),
    // );

    return {
      ...message,
      //toolInvocations: sanitizedToolInvocations,
    };
  });

  return messagesBySanitizedToolInvocations.filter(
    (message) =>
      hasMessageContent(message)
  );
}

export function getDocumentTimestampByIndex(
  documents: Array<Document>,
  index: number,
) {
  if (!documents) return new Date();
  if (index > documents.length) return new Date();

  return documents[index].createdAt;
}

export function capitalizeFirstLetter(str: string) {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
}

const fileIdRegex = /{\s*"*fileid"*\s*:\s*"*([^\s"]+)"*\s*}/gi

export function replaceFileIdsWithNames(
  message: string,
  matterFiles: MatterFileInfo[],
  klSources: KLSource[]
) {
  return message.replace(fileIdRegex, (match, fileId) => {
    if (isKLSourceCitationId(fileId)) {
      const sourceId = parseKLSourceCitationSourceId(fileId)
      const klSource = klSources.find(source => source.id === sourceId)
      if (klSource) {
        return isKLSourceDocument(klSource) ? `[${klSource.metadata.filename}${klSource.metadata.fileExtension}](//)` : `[${(klSource as KLSourceWebPage).metadata.url}](${(klSource as KLSourceWebPage).metadata.url})`
      }
      console.log('kl source not found')
      return match
    }

    // Find the corresponding file info by fileId
    const fileInfo = matterFiles.find(file => file.id === fileId)
    // If found, return the fileName (and fileExtension if needed)
    if (fileInfo) {
      return `[${fileInfo.fileName}${fileInfo.fileExtension}](//)`
    }
    console.log('file not found')
    // If not found, return the original match
    return match
  })
}

export const buildFullFileNameFromFileInfo = (file: MatterFileInfo | KLSource | undefined) => {
  if (!file) {
    return ''
  }

  if (isKLSource(file)) {
    if (isKLSourceDocument(file)) {
      return concatenateFileNameAndExtension({ fileName: file.metadata.filename, extension: file.metadata.fileExtension })
    } else {
      return `${file.id}.md`
    }
  }
  return concatenateFileNameAndExtension({ fileName: file.fileName, extension: file.fileExtension })
}

function concatenateFileNameAndExtension({ fileName, extension }: { fileName: string | undefined, extension: string | undefined }) {
  return `${fileName || ''}${extension || ''}`
}